import {
  Avatar,
  Box,
  Button,
  HStack,
  Link,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { walletAddressPreview } from "@models/utils";
import { useUserStore } from "@providers/auth";
import { ReactNode, useMemo } from "react";
import { MoongateLogoDarkMode, MoongateLogoLightMode } from "../../assets";
import { useMobile } from "../../hooks/mobile";
import { CloseIcon, MenuIcon, MoongateLogoIcon } from "../../icons";
import Logout from "../Logout";
import { NextImage } from "../NextImage/NextImage";
import ToggleTheme from "../ToggleTheme";

export const HeaderLogo = ({ url }: { url: string }) => {
  const { isMobile } = useMobile();
  const logo = useColorModeValue(MoongateLogoLightMode, MoongateLogoDarkMode);

  return (
    <Link href={url}>
      {isMobile ? (
        <div className="text-[#00A28D]">
          <MoongateLogoIcon />
        </div>
      ) : (
        <NextImage
          className="w-56"
          src={logo}
          alt="moongate"
          layout="responsive"
        />
      )}
    </Link>
  );
};

export const HeaderAvatar = ({ url }: { url?: string }) => {
  const user = useUserStore((state) => state.user);
  const name = useMemo(() => {
    try {
      if (user?.username) return user.username;
      if (user?.email) return user.email;
      return walletAddressPreview(user?.evmWallets[0] || "");
    } catch (error) {
      return "";
    }
  }, [user]);

  return user ? (
    <Link href={url}>
      <Button
        size="md"
        variant="outline"
        leftIcon={<Avatar size="xs" name={name} />}
        fontSize="xs"
      >
        {name}
      </Button>
    </Link>
  ) : (
    <></>
  );
};

const Header = ({
  maxW: maxW = "1280px",
  allowToggleTheme: allowToggleTheme = true,
  iconSize: iconSize = "20px",
  collapseContent,
  landingUrl = "/",
  appUrl = "/",
}: {
  maxW?: string;
  allowToggleTheme?: boolean;
  iconSize?: string;
  collapseContent?: ReactNode;
  landingUrl?: string;
  appUrl?: string;
}) => {
  const user = useUserStore((state) => state.user);
  const authenticating = useUserStore((state) => state.authenticating);
  const { isMobile, initialized } = useMobile();
  const { isOpen, getDisclosureProps, getButtonProps } = useDisclosure();
  const buttonProps = getButtonProps();
  const disclosureProps = getDisclosureProps();
  const icon = useMemo(() => {
    return isOpen ? (
      <CloseIcon boxSize={iconSize} />
    ) : (
      <MenuIcon boxSize={iconSize} />
    );
  }, [isOpen]);

  if (authenticating || !initialized) return <></>;
  return (
    <>
      <Box
        as="header"
        className="z-50 mx-auto flex w-full flex-row items-center justify-between px-4 pb-8 pt-4 lg:px-0 sm:py-4"
        maxW={maxW}
      >
        <HeaderLogo url={landingUrl} />

        <HStack>
          <HeaderAvatar url={appUrl} />
          {!isMobile ? (
            <>
              {allowToggleTheme && <ToggleTheme />}
              <Logout />
            </>
          ) : (
            (collapseContent || allowToggleTheme || user) && (
              <Button size="md" variant="outline" {...buttonProps}>
                {icon}
              </Button>
            )
          )}
        </HStack>
      </Box>

      {isMobile && (
        <nav {...disclosureProps} className="flex flex-col px-6 mb-8 w-full ">
          {collapseContent && <div className="w-full">{collapseContent}</div>}
          <div className="flex flex-row w-full gap-2 mt-4 first:mt-0">
            {allowToggleTheme && (
              <ToggleTheme className="w-full" showtext="true" />
            )}
            <Logout className="w-full" showtext="true" />
          </div>
        </nav>
      )}
    </>
  );
};
export default Header;
